'use client';

import { isPathMatch } from '@/@studio/utils';
import { DEV_PORTAL_NAV, showsDevPortalInNav } from '@/lib/dev-portal/nav-access';
import { cn, Link, Tooltip } from '@heroui/react';
import { Icon } from '@iconify/react';
import { usePathname, useRouter } from 'next/navigation';
import { useAccessContext } from '../hooks/use-access-context';
import { NavRailItem } from './nav-rail-item';

/**
 * The Developer Portal row, bottom left above the profile menu. Not one of
 * the NAV_SECTIONS stages (Field, Analyze, Connect, Report): it is reference
 * for people building on the API, so it sits with the account controls.
 */
export function NavDeveloperLink({ isCollapsed }: { isCollapsed: boolean }) {
    const ctx = useAccessContext();
    const pathname = usePathname();
    const router = useRouter();

    if (!showsDevPortalInNav(ctx)) return null;
    const isActive = isPathMatch(DEV_PORTAL_NAV.href, pathname);

    if (isCollapsed) {
        return (
            <Tooltip
                placement="right"
                content={
                    <div className="px-0.5 py-0.5">
                        <p className="text-[13px] font-semibold text-white">{DEV_PORTAL_NAV.label}</p>
                        <p className="text-[12px] text-white/70">API reference and guides.</p>
                    </div>
                }
                classNames={{ content: 'rounded-xl bg-[#192138] px-3 py-2 shadow-lg' }}
            >
                <div className="w-full">
                    <NavRailItem
                        icon={DEV_PORTAL_NAV.icon}
                        label={DEV_PORTAL_NAV.railLabel}
                        isActive={isActive}
                        onClick={() => router.push(DEV_PORTAL_NAV.href)}
                    />
                </div>
            </Tooltip>
        );
    }

    return (
        <div className="w-full px-1">
            <Link
                href={DEV_PORTAL_NAV.href}
                aria-current={isActive ? 'page' : undefined}
                className={cn('flex items-center gap-2 rounded-large px-2 py-1 hover:!opacity-100', {
                    'bg-[#F3F8FF] font-semibold text-foreground': isActive,
                    'text-default-600 hover:bg-[#F3F8FF]': !isActive,
                })}
            >
                <span className="flex h-6 w-6 items-center justify-center rounded-lg bg-foreground text-white">
                    <Icon icon={`lucide:${DEV_PORTAL_NAV.icon}`} width={14} height={14} />
                </span>
                <span className="text-[13px]">{DEV_PORTAL_NAV.label}</span>
            </Link>
        </div>
    );
}

NavDeveloperLink.displayName = 'NavDeveloperLink';
